import { Box, Snackbar, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { FilesService } from '../../services/files';
import { LearnersService } from '../../services/learners';
import useStore from '../../store/store';
import { allowedCountries } from '../../helpers';
import { parsePhoneNumberFromString } from 'libphonenumber-js';


function DropFile({ setUserData, setIsInviteDialogOpen, closeDropFile }) {
  const [excelFile, setExcelFile] = useState(null);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const setSnackbarContent = useStore((store) => store.setSnackbarContent);

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    setIsError(false);
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: '.xlsx, .xls',
    onDrop: (acceptedFiles) => {
      if (acceptedFiles && acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        if (
          file.type ===
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        ) {
          setExcelFile(file);
        } else {
          alert('Please upload a valid Excel file.');
        }
      }
    },
  });

  const guessPhoneNumber = (raw) => {
    let number = raw.replace(/\D/g, ""); // keep only digits
  
    // --- France Special Cases ---
    // Case 1: French numbers with "33" prefix but misparsed (e.g. 33624437468)
    if (number.startsWith("33") && number.length === 11) {
      const french = parsePhoneNumberFromString("+" + number, "FR");
      if (french?.isValid()) {
        return french.number; // returns +336...
      }
    }
  
    // Case 2: Local French numbers starting with 0 (e.g. 0624437469)
    if (number.startsWith("0") && number.length === 10) {
      const french = parsePhoneNumberFromString(number, "FR");
      if (french?.isValid()) {
        return french.number; // +336...
      }
    }
  
    // Case 3: Missing leading 0 but clearly French (9 digits starting with [67])
    // e.g. "624437469" → "+33624437469"
    if (number.length === 9 && /^[67]/.test(number)) {
      const french = parsePhoneNumberFromString("0" + number, "FR");
      if (french?.isValid()) {
        return french.number; // +336...
      }
    }
  
    // --- Generic Cases ---
    // Try parsing with all allowed countries
    for (const country of allowedCountries) {
      try {
        const phone = parsePhoneNumberFromString(raw, country);
        if (phone?.isValid()) {
          return phone.number; // normalized E.164 (+33..., +43..., +44..., +31...)
        }
      } catch(e) {
        console.error(e)
        setSnackbarContent(
          'error',
          `Error processing learner with number: ${raw}. Details: ${e?.message}`
        );

        return "";
      }
    }
  
    return ""; // not recognized
  };

  const ValidateData = async () => {
    try {
      const res = await LearnersService.validateTemplate(excelFile);
      
      // catch custom error with duplicates
      if (res.data?.data?.error) {
        setSnackbarContent('error', res.data?.data?.error || 'Error');
      }

      const learners = res.data?.data?.data.map(learner => {
        const phoneNumber = guessPhoneNumber(learner.phoneNumber || '');

        return {
          ...learner,
          phoneNumber,
        }
      });

      return learners;
    } catch (e) {
      console.log('Error:', e);
      if (e?.response?.data?.message) {
        setErrorMessage(e.response.data.message);
      } else {
        setErrorMessage('Error due inviting');
      }
      setIsError(true);
    }
  };

  const uploadFile = async () => {
    setIsError(false);
    setIsInviteDialogOpen(false);
    setUserData([]);
    if (excelFile) {
      try {
        await FilesService.uploadFile(excelFile, 1);
        const fileData = await ValidateData(excelFile);
        setUserData(fileData);
        setIsInviteDialogOpen(true);
        closeDropFile();
      } catch (e) {
        console.log('Error: ', e.response.data.message);
        if (e?.response?.data?.message) {
          setErrorMessage(e.response.data.message);
        } else {
          setErrorMessage('Error due inviting');
        }

        setIsError(true);
      }
    }
  };

  useEffect(() => {
    uploadFile();
  }, [excelFile]);

  return (
    <Box {...getRootProps()}>
      <input {...getInputProps()} />
      {excelFile ? (
        <Typography>Fichier Excel téléchargé: {excelFile.name}</Typography>
      ) : (
        <Typography className="drop-area">
          Faites glisser et déposez un fichier Excel ici, ou cliquez pour
          sélectionner
        </Typography>
      )}

      <Snackbar
        open={isError}
        autoHideDuration={6000}
        onClose={handleClose}
        message={errorMessage}
      />
    </Box>
  );
}

export default DropFile;
